/**
 * The review list for the `proposal:relationship` output kind on the generic
 * agent report page: every relationship this agent proposed that waits for a
 * person, with accept (the relationship is created, or a package of the two
 * items — migration 948) and dismiss, and the
 * recently reviewed ones with Reopen for whoever reviewed them. Accept and
 * dismiss go through the same store functions every other relationship
 * surface uses, so the triggers that enforce reviewers and stamp decision
 * records fire the same way.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Loader2, Minus, Sparkles, X } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Callout } from '../../ds';
import { useNexusRelationshipStore } from '../../../stores/prioritization/nexusRelationshipStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { ReverseActionButton } from '../../reversals/ReverseActionButton';
import { reopenNexusProposal } from '../../../lib/reversals/reversalsService';
import type { NexusRelationshipProposal } from '../../../lib/nexusAgentService';
import { when, type OutputReviewProps } from './types';
import { useOpenRelationshipProposals } from './useOpenRelationshipProposals';

const EMPTY: NexusRelationshipProposal[] = [];

/** "A → B" for a directed relationship (by the source item's role), "A — B" otherwise. */
function ends(p: NexusRelationshipProposal): { from: string; to: string; directed: boolean } {
  const directed = p.directionality === 'directed';
  const flip = directed && p.sourceItemRole === 'target';
  return { from: flip ? p.targetItemId : p.sourceItemId, to: flip ? p.sourceItemId : p.targetItemId, directed };
}

export function RelationshipProposalsReview({ def, workspaceId, mayReview, onChanged, titleOf }: OutputReviewProps) {
  const userId = useUserStore((s) => s.userId);
  const accept = useNexusRelationshipStore((s) => s.accept);
  const dismiss = useNexusRelationshipStore((s) => s.dismiss);
  const fetchProposals = useNexusRelationshipStore((s) => s.fetchProposals);
  const fetchHistory = useNexusRelationshipStore((s) => s.fetchHistory);
  const history = useNexusRelationshipStore((s) => s.byWorkspace[workspaceId]?.history ?? EMPTY);
  const open = useOpenRelationshipProposals(def.agentType);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<NexusRelationshipProposal | null>(null);

  useEffect(() => { void fetchHistory(workspaceId, 50); }, [workspaceId, fetchHistory]);
  const reviewed = useMemo(() => history.filter((p) => p.proposedByAgent === def.agentType).slice(0, 10), [history, def.agentType]);

  const run = async (id: string, act: () => Promise<{ success: boolean; error?: string }>, onOk?: () => void) => {
    setBusyId(id);
    setError(null);
    const result = await act();
    if (!result.success) setError(result.error ?? 'Not saved');
    else onOk?.();
    onChanged();
    setBusyId(null);
  };
  const reload = () => { void fetchProposals(workspaceId); void fetchHistory(workspaceId, 50); onChanged(); };

  const pair = (p: NexusRelationshipProposal) => {
    const e = ends(p);
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5 text-sm">
        <Link to={`/prioritization/items/${e.from}`} className="text-[var(--text-pri)] underline">{titleOf(e.from)}</Link>
        {e.directed ? <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)]" /> : <Minus className="h-3.5 w-3.5 text-[var(--text-ter)]" />}
        <Link to={`/prioritization/items/${e.to}`} className="text-[var(--text-pri)] underline">{titleOf(e.to)}</Link>
      </span>
    );
  };

  return (
    <div className="space-y-2">
      {error && <Callout tone="coral">{error}</Callout>}
      {accepted && (
        <Callout tone="neutral">
          Accepted. The {accepted.relationshipType === 'package' ? 'package' : 'relationship'} now exists between{' '}
          <Link to={`/prioritization/items/${accepted.sourceItemId}`} className="underline">{titleOf(accepted.sourceItemId)}</Link> and{' '}
          <Link to={`/prioritization/items/${accepted.targetItemId}`} className="underline">{titleOf(accepted.targetItemId)}</Link>.
        </Callout>
      )}
      {!mayReview && open.length > 0 && (
        <Callout tone="neutral">Reviewing what {def.name} proposes needs one of its review roles. A workspace administrator can grant one.</Callout>
      )}
      {open.length === 0 ? (
        <div className="card text-center py-10">
          <Sparkles className="h-6 w-6 mx-auto text-[var(--text-ter)]" />
          <p className="text-sm font-medium text-[var(--text-pri)] mt-2">Nothing waiting</p>
          <p className="text-xs text-[var(--text-ter)] mt-1">Run {def.name} over a scope to look for relationships between items.</p>
        </div>
      ) : open.map((p) => (
        <div key={p.id} className="card space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-pri)] border border-[var(--slate)] capitalize">
              {p.relationshipType}{p.relationshipSubtype ? ` · ${p.relationshipSubtype.replace(/_/g, ' ')}` : ''}
            </span>
            <span className="text-xs text-[var(--text-ter)]">{p.confidence} confidence</span>
            <span className="ml-auto text-xs text-[var(--text-ter)]">{when(p.createdAt)}</span>
          </div>
          {pair(p)}
          {p.rationale && <p className="text-xs text-[var(--text-ter)] italic">"{p.rationale}"</p>}
          {mayReview && userId && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" disabled={busyId === p.id} onClick={() => void run(p.id, () => dismiss({ proposalId: p.id, userId, workspaceId }))}>
                <X className="h-3.5 w-3.5" /> Dismiss
              </Button>
              <Button variant="primary" size="sm" disabled={busyId === p.id} onClick={() => void run(p.id, () => accept({ proposalId: p.id, userId, workspaceId }), () => setAccepted(p))}>
                {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept
              </Button>
            </div>
          )}
        </div>
      ))}
      {reviewed.length > 0 && (
        <div className="pt-3">
          <p className="text-xs font-medium text-[var(--text-sec)] mb-1">Recently reviewed</p>
          <div className="divide-y divide-[var(--rule)]">
            {reviewed.map((p) => (
              <div key={p.id} className="py-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-[var(--text-sec)]">{p.status === 'accepted' ? 'Accepted' : 'Dismissed'} · {p.relationshipType}</span>
                {pair(p)}
                <span className="ml-auto text-[var(--text-ter)]">{when(p.reviewedAt ?? p.createdAt)}</span>
                {userId && p.reviewedByUserId === userId && (
                  <ReverseActionButton
                    label="Reopen"
                    confirmTitle="Reopen this relationship"
                    explain="This returns the proposal to the waiting list for review again."
                    run={(reason) => reopenNexusProposal(p.id, reason)}
                    onDone={reload}
                    variant="ghost"
                    size="sm"
                    className="!px-2 !py-1 !text-xs"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
